import { z } from 'zod';

/**
 * Polskie identyfikatory firm (prośba Sebastiana 6.10.2026: dane firmy po NIP przy dodawaniu klienta).
 *
 * NIP: 10 cyfr, ostatnia to suma kontrolna (wagi 6, 5, 7, 2, 3, 4, 5, 6, 7; reszta z dzielenia przez 11, reszta 10
 * = numer nieprawidłowy). Zapis wejściowy może mieć myślniki, spacje, kropki i prefiks „PL” („PL 526-025-02-74”);
 * po normalizacji zostaje 10 cyfr („5260250274”) — tak zapisujemy NIP w bazie i wysyłamy do rejestrów.
 * REGON: 9 albo 14 cyfr z sumą kontrolną. KRS: 10 cyfr (bez sumy kontrolnej).
 */

const NIP_WEIGHTS = [6, 5, 7, 2, 3, 4, 5, 6, 7] as const;
const REGON9_WEIGHTS = [8, 9, 2, 3, 4, 5, 6, 7] as const;
const REGON14_WEIGHTS = [2, 4, 8, 5, 0, 9, 7, 3, 6, 1, 2, 4, 8] as const;

export const NIP_MESSAGES = {
  required: 'Podaj NIP',
  length: 'NIP ma 10 cyfr',
  checksum: 'Nieprawidłowy NIP — sprawdź cyfry',
  taxId: 'Nieprawidłowy NIP. Numer zagraniczny wpisz z kodem kraju, np. DE123456789',
} as const;

/** Usuwa spacje, myślniki, kropki i prefiks „PL”. Zwraca 10 cyfr albo `null` (bez sprawdzania sumy kontrolnej). */
export function normalizeNip(input: string | null | undefined): string | null {
  if (typeof input !== 'string') return null;
  const compact = input.replace(/[\s .\-–]/g, '').toUpperCase();
  const digits = compact.startsWith('PL') ? compact.slice(2) : compact;
  return /^\d{10}$/.test(digits) ? digits : null;
}

/** Suma kontrolna NIP (dla 10 cyfr). „0000000000” nie jest numerem. */
export function nipChecksumOk(digits: string): boolean {
  if (!/^\d{10}$/.test(digits) || /^0+$/.test(digits)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += Number(digits[i]) * (NIP_WEIGHTS[i] as number);
  const check = sum % 11;
  return check !== 10 && check === Number(digits[9]);
}

/** Poprawny polski NIP w dowolnym dopuszczalnym zapisie. */
export function isValidNip(input: string | null | undefined): boolean {
  const n = normalizeNip(input);
  return n !== null && nipChecksumOk(n);
}

/** Wyjaśnienie, czemu NIP jest błędny (`null` = poprawny). */
export function nipProblem(input: string | null | undefined): string | null {
  if (typeof input !== 'string' || input.trim() === '') return NIP_MESSAGES.required;
  const n = normalizeNip(input);
  if (!n) return NIP_MESSAGES.length;
  return nipChecksumOk(n) ? null : NIP_MESSAGES.checksum;
}

/** „5260250274” → „526-025-02-74” (zapis do wyświetlenia; w bazie same cyfry). */
export function formatNip(nip: string | null | undefined): string {
  const n = normalizeNip(nip);
  if (!n) return nip ?? '';
  return `${n.slice(0, 3)}-${n.slice(3, 6)}-${n.slice(6, 8)}-${n.slice(8)}`;
}

function weightedMod11(digits: string, weights: readonly number[]): number {
  let sum = 0;
  for (let i = 0; i < weights.length; i++) sum += Number(digits[i]) * (weights[i] as number);
  const r = sum % 11;
  return r === 10 ? 0 : r;
}

/** REGON 9 albo 14 cyfr z poprawną sumą kontrolną. */
export function isValidRegon(input: string | null | undefined): boolean {
  if (typeof input !== 'string') return false;
  const d = input.replace(/[\s-]/g, '');
  if (/^\d{9}$/.test(d)) return weightedMod11(d, REGON9_WEIGHTS) === Number(d[8]);
  if (/^\d{14}$/.test(d)) {
    return weightedMod11(d, REGON9_WEIGHTS) === Number(d[8]) && weightedMod11(d, REGON14_WEIGHTS) === Number(d[13]);
  }
  return false;
}

/** NIP polski (wymagany): wynik = 10 cyfr. */
export const nipSchema = z.string({ required_error: NIP_MESSAGES.required }).transform((value, ctx) => {
  const problem = nipProblem(value);
  if (problem) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem });
    return z.NEVER;
  }
  return normalizeNip(value) as string;
});

/**
 * Numer podatkowy klienta (`clients.company.nip`): polski NIP (sprawdzany i zapisywany jako 10 cyfr, także z prefiksem
 * „PL”) albo zagraniczny numer VAT UE z kodem kraju („DE123456789”, „CZ12345678”) — klienci spoza Polski
 * (np. TeckWrap Europe). Sam ciąg cyfr innej długości niż 10 jest błędem.
 */
export const taxIdSchema = z.string().transform((value, ctx) => {
  const compact = value.replace(/[\s .\-–]/g, '').toUpperCase();
  if (/^(PL)?\d+$/.test(compact)) {
    const problem = nipProblem(value);
    if (problem) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: problem === NIP_MESSAGES.length ? NIP_MESSAGES.taxId : problem,
      });
      return z.NEVER;
    }
    return normalizeNip(value) as string;
  }
  if (/^[A-Z]{2}[0-9A-Z+*]{2,13}$/.test(compact) && !compact.startsWith('PL')) return compact;
  ctx.addIssue({ code: z.ZodIssueCode.custom, message: NIP_MESSAGES.taxId });
  return z.NEVER;
});

export const regonSchema = z
  .string()
  .transform((v) => v.replace(/[\s-]/g, ''))
  .refine(isValidRegon, { message: 'Nieprawidłowy REGON (9 albo 14 cyfr)' });

export const krsSchema = z
  .string()
  .transform((v) => v.replace(/\s/g, ''))
  .refine((v) => /^\d{10}$/.test(v), { message: 'Numer KRS ma 10 cyfr' });

/** Wersja opcjonalna pola tekstowego: pusty tekst = brak wartości (`undefined`), inaczej walidacja `schema`. */
export function optionalIdentifier<T>(schema: z.ZodType<T, z.ZodTypeDef, string>) {
  return z
    .string()
    .transform((value, ctx): T | undefined => {
      if (value.trim() === '') return undefined;
      const parsed = schema.safeParse(value);
      if (!parsed.success) {
        for (const issue of parsed.error.issues) ctx.addIssue({ code: z.ZodIssueCode.custom, message: issue.message });
        return z.NEVER;
      }
      return parsed.data;
    })
    .optional();
}

export const optionalTaxIdSchema = optionalIdentifier(taxIdSchema);
export const optionalRegonSchema = optionalIdentifier(regonSchema);
export const optionalKrsSchema = optionalIdentifier(krsSchema);
